import React, { useState } from 'react';
import { 
  GitCompare, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  HelpCircle, 
  Loader2, 
  AlertCircle,
  ChevronDown,
  ChevronRight,
  FileText
} from 'lucide-react';
import { 
  useGetCrossPageValidationQuery,
  type TitleblockInconsistencyItem,
  type CrossPageQuestionGroup,
  type CrossPageQuestionItem,
  type PageReferenceItem,
} from '../../redux/api/qaApi';
import { useAppSelector } from '../../redux/hooks';

const CrossPageTab: React.FC = () => {
  // Get site_id from Redux
  const currentSite = useAppSelector((state) => state.site.currentSite);
  const siteId = currentSite?.site_id;

  // Accordion state - track which sections are expanded
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set()); // All closed by default

  const toggleSection = (sectionId: string) => {
    setExpandedSections(prev => {
      const newSet = new Set(prev);
      if (newSet.has(sectionId)) {
        newSet.delete(sectionId);
      } else {
        newSet.add(sectionId);
      }
      return newSet;
    });
  };

  // Fetch cross-page validation data
  const { data: validationData, isLoading, error } = useGetCrossPageValidationQuery(
    siteId ? { site_id: siteId } : undefined,
    { skip: !siteId }
  );

  const titleblockInconsistencies = validationData?.titleblock_inconsistencies || [];
  const inconsistencyCount = validationData?.titleblock_inconsistency_count || 0;
  const totalFailed = validationData?.total_failed || 0;
  const totalPassed = validationData?.total_passed || 0;
  const totalCA = validationData?.total_ca || 0;
  const totalNoResponse = validationData?.total_no_response || 0;
  const failedQuestions = validationData?.failed_questions || [];
  const passedQuestions = validationData?.passed_questions || [];
  const caQuestions = validationData?.ca_questions || [];
  const noResponseQuestions = validationData?.no_response_questions || [];
  const pageReferences = validationData?.page_references || [];

  const hasNoData =
    inconsistencyCount === 0 &&
    totalFailed === 0 &&
    totalPassed === 0 &&
    totalCA === 0 &&
    totalNoResponse === 0 &&
    titleblockInconsistencies.length === 0 &&
    failedQuestions.length === 0 &&
    passedQuestions.length === 0 &&
    caQuestions.length === 0 &&
    noResponseQuestions.length === 0 &&
    pageReferences.length === 0;

  // Format field name for display
  const formatFieldName = (field: string): string => {
    return field
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1))
      .join(' ');
  };

  return (
    <div className="eg-page-shell">
      {/* Header */}
      <div className="eg-toolbar-header p-4">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold eg-text-primary mb-1">Cross-Page Validation</h2>
            <p className="text-sm eg-text-secondary">Consistency checks across multiple pages</p>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="eg-page-content p-4">
        {!siteId ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 eg-text-secondary mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">No Site Selected</p>
              <p className="text-sm eg-text-secondary mt-1">
                Please select a site from the sidebar to view cross-page validation
              </p>
            </div>
          </div>
        ) : isLoading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-engramiq-green" />
            <span className="ml-3 eg-text-primary">Loading cross-page validation...</span>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-12">
            <AlertCircle className="w-8 h-8 text-accent-red mr-3" />
            <div className="eg-text-primary">
              <p className="font-semibold">Error loading cross-page validation</p>
              <p className="text-sm eg-text-secondary mt-1">
                {error && 'data' in error ? JSON.stringify(error.data) : 'Unknown error'}
              </p>
            </div>
          </div>
        ) : hasNoData ? (
          <div className="flex flex-col items-center justify-center py-12">
            <GitCompare className="w-16 h-16 eg-text-secondary mb-4" />
            <p className="text-lg eg-text-primary mb-2">No cross page validations available</p>
            <p className="text-sm eg-text-secondary">
              No cross-page validation data found for the selected site. Run or import QA results to see validations here.
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {/* Titleblock Consistency Accordion */}
            <AccordionSection
              id="titleblock"
              title="Titleblock Consistency"
              icon={<GitCompare size={20} className="text-engramiq-green" />}
              count={inconsistencyCount}
              isExpanded={expandedSections.has('titleblock')}
              onToggle={() => toggleSection('titleblock')}
              badgeColor="orange"
            >
              {titleblockInconsistencies.length > 0 ? (
                <div className="space-y-4">
                  {titleblockInconsistencies.map((item) => (
                    <div key={item.field} className="border border-orange-400/30 bg-orange-400/10 rounded-lg p-4">
                      <div className="flex items-center gap-2 mb-3">
                        <AlertTriangle size={16} className="text-orange-400" />
                        <span className="font-medium text-orange-400 capitalize">
                          Inconsistent {formatFieldName(item.field)}
                        </span>
                      </div>
                      <div className="space-y-2">
                        {item.values.map((valueItem, idx) => (
                          <div key={`${item.field}-${idx}`} className="flex items-start gap-3">
                            <span className="font-mono text-sm px-2 py-1 rounded eg-text-primary" style={{ background: 'var(--color-surface-elevated)' }}>
                              {valueItem.value}
                            </span>
                            <div className="flex flex-wrap gap-1">
                              {valueItem.pages.map((pageNum) => (
                                <span
                                  key={`${item.field}-${idx}-${valueItem.value}-${pageNum}`}
                                  role="button"
                                  tabIndex={0}
                                  onClick={() => {
                                    window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', { detail: { page: pageNum } }));
                                  }}
                                  onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                                  className="px-2 py-0.5 bg-blue-400/20 text-blue-400 rounded text-xs border border-blue-400/30 cursor-pointer hover:bg-blue-400/30 transition-colors"
                                >
                                  P{pageNum}
                                </span>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="bg-green-400/20 border border-green-400/30 rounded-lg p-6">
                  <div className="flex items-center gap-2">
                    <CheckCircle size={20} className="text-green-400" />
                    <p className="text-green-400 font-medium">All titleblock fields are consistent across pages.</p>
                  </div>
                </div>
              )}
            </AccordionSection>

            {/* Failed Cross-Sheet Checks Accordion */}
            {failedQuestions.length > 0 && (
              <AccordionSection
                id="failed"
                title="Failed Cross-Sheet Checks"
                icon={<XCircle size={20} className="text-red-400" />}
                count={totalFailed}
                isExpanded={expandedSections.has('failed')}
                onToggle={() => toggleSection('failed')}
                badgeColor="red"
              >
              <QuestionSection groups={failedQuestions} />
            </AccordionSection>
            )}

            {/* Passed Cross-Sheet Checks Accordion */}
            {passedQuestions.length > 0 && (
              <AccordionSection
                id="passed"
                title="Passed Cross-Sheet Checks"
                icon={<CheckCircle size={20} className="text-green-400" />}
                count={totalPassed}
                isExpanded={expandedSections.has('passed')}
                onToggle={() => toggleSection('passed')}
                badgeColor="green"
              >
                <QuestionSection groups={passedQuestions} />
              </AccordionSection>
            )}

            {/* Cannot Assess Accordion */}
            {caQuestions.length > 0 && (
              <AccordionSection
                id="ca"
                title="Cannot Assess"
                icon={<HelpCircle size={20} className="text-yellow-400" />}
                count={totalCA}
                isExpanded={expandedSections.has('ca')}
                onToggle={() => toggleSection('ca')}
                badgeColor="yellow"
              >
                <QuestionSection groups={caQuestions} />
              </AccordionSection>
            )}

            {/* No Response Questions Accordion */}
            {noResponseQuestions.length > 0 && (
              <AccordionSection
                id="noresponse"
                title="No Response"
                icon={<FileText size={20} className="eg-text-secondary" />}
                count={totalNoResponse}
                isExpanded={expandedSections.has('noresponse')}
                onToggle={() => toggleSection('noresponse')}
                badgeColor="grey"
              >
                <div className="space-y-2">
                  {noResponseQuestions.map((q) => (
                    <div key={q.question_id} className="text-sm eg-text-secondary py-2 px-3 eg-card-elevated">
                      <span className="eg-text-primary font-medium mr-2">Q{q.question_id}</span>
                      {q.check_description}
                    </div>
                  ))}
                </div>
              </AccordionSection>
            )}

            {/* Page References Accordion */}
            {pageReferences.length > 0 && (
              <AccordionSection
                id="pagereferences"
                title="Page References"
                icon={<FileText size={20} className="text-engramiq-green" />}
                count={pageReferences.length}
                isExpanded={expandedSections.has('pagereferences')}
                onToggle={() => toggleSection('pagereferences')}
                badgeColor="blue"
              >
                <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                  {pageReferences.map((ref, refIdx) => (
                    <div key={`ref-${refIdx}-${ref.reference_text}`} className="eg-card-elevated p-3">
                      <p className="font-medium eg-text-primary text-sm mb-2">{ref.reference_text}</p>
                      <div className="flex flex-wrap gap-1">
                        {ref.pages.map((p) => (
                          <span
                            key={`ref-${refIdx}-page-${p}`}
                            role="button"
                            tabIndex={0}
                            onClick={() => {
                              window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', { detail: { page: p } }));
                            }}
                            onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                            className="px-2 py-0.5 bg-blue-400/20 text-blue-400 rounded text-xs border border-blue-400/30 cursor-pointer hover:bg-blue-400/30 transition-colors"
                          >
                            P{p}
                          </span>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </AccordionSection>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

/** Accordion Section Component */
interface AccordionSectionProps {
  id: string;
  title: string;
  icon: React.ReactNode;
  count: number;
  isExpanded: boolean;
  onToggle: () => void;
  badgeColor: 'red' | 'green' | 'yellow' | 'orange' | 'blue' | 'grey';
  children: React.ReactNode;
}

const AccordionSection: React.FC<AccordionSectionProps> = ({
  title,
  icon,
  count,
  isExpanded,
  onToggle,
  badgeColor,
  children,
}) => {
  const badgeColors = {
    red: 'bg-red-400/20 text-red-400 border-red-400/30',
    green: 'bg-green-400/20 text-green-400 border-green-400/30',
    yellow: 'bg-yellow-400/20 text-yellow-400 border-yellow-400/30',
    orange: 'bg-orange-400/20 text-orange-400 border-orange-400/30',
    blue: 'bg-blue-400/20 text-blue-400 border-blue-400/30',
    grey: 'bg-engramiq-bright-grey/20 eg-text-secondary border-engramiq-bright-grey/30',
  };

  return (
    <div className="eg-card-elevated overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full px-6 py-4 flex items-center justify-between hover:brightness-105 transition-all"
      >
        <div className="flex items-center gap-3">
          {isExpanded ? (
            <ChevronDown className="w-5 h-5 text-engramiq-green flex-shrink-0" />
          ) : (
            <ChevronRight className="w-5 h-5 text-engramiq-green flex-shrink-0" />
          )}
          {icon}
          <h3 className="text-lg font-semibold eg-text-primary">{title}</h3>
          <span className={`px-2 py-1 rounded text-xs font-medium border ${badgeColors[badgeColor]}`}>
            {count}
          </span>
        </div>
      </button>
      {isExpanded && (
        <div className="px-6 py-4 border-t" style={{ borderColor: 'var(--color-overlay-light)' }}>
          {children}
        </div>
      )}
    </div>
  );
};

/** Question Section Component - Groups questions by category */
interface QuestionSectionProps {
  groups: CrossPageQuestionGroup[];
}

const QuestionSection: React.FC<QuestionSectionProps> = ({ groups }) => {
  return (
    <div className="space-y-4">
      {groups.map((group) => (
        <div key={group.category} className="mb-4 last:mb-0">
          <h4 className="text-sm font-semibold eg-text-secondary uppercase tracking-wide mb-3">
            {group.category}
            <span className="ml-2 eg-text-secondary font-normal normal-case">({group.questions.length})</span>
          </h4>
          <div className="space-y-2">
            {group.questions.map((q) => (
              <QuestionRow key={`${q.question_id}-${q.page_number}`} question={q} />
            ))}
          </div>
        </div>
      ))}
    </div>
  );
};

/** Question Row Component */
interface QuestionRowProps {
  question: CrossPageQuestionItem;
}

const QuestionRow: React.FC<QuestionRowProps> = ({ question }) => {
  const badgeClass =
    question.answer_type === 'pass'
      ? 'bg-green-400/20 text-green-400 border-green-400/30'
      : question.answer_type === 'fail'
      ? 'bg-red-400/20 text-red-400 border-red-400/30'
      : 'bg-yellow-400/20 text-yellow-400 border-yellow-400/30';

  return (
    <div className="eg-card-elevated p-4">
      <div className="flex items-start gap-3">
        <span className={`px-2 py-0.5 rounded text-xs font-medium flex-shrink-0 mt-0.5 border ${badgeClass}`}>
          {question.answer_type}
        </span>
        <div className="flex-1 min-w-0">
          <p className="font-medium eg-text-primary text-sm">
            {question.check_description}
          </p>
          {question.description && (
            <p className="text-sm eg-text-secondary mt-1">{question.description}</p>
          )}
          <div className="flex flex-wrap items-center gap-3 mt-2">
            {question.applicable_sheet_types && question.applicable_sheet_types.length > 0 && (
              <span className="text-xs eg-text-secondary flex items-center gap-1">
                Sheets:{' '}
                {question.applicable_sheet_types.map((st, stIdx) => (
                  <span key={`${question.question_id}-sheet-${stIdx}-${st}`} className="px-1.5 py-0.5 bg-purple-400/20 text-purple-400 rounded text-xs border border-purple-400/30">
                    {st}
                  </span>
                ))}
              </span>
            )}
            {question.pages_referenced && question.pages_referenced.length > 0 && (
              <span className="text-xs eg-text-secondary flex items-center gap-1">
                Pages:{' '}
                {question.pages_referenced.map((p, pIdx) => {
                  const bbox = p === question.page_number ? question.bounding_box ?? undefined : undefined;
                  return (
                    <span
                      key={`${question.question_id}-page-ref-${pIdx}-${p}`}
                      role="button"
                      tabIndex={0}
                      onClick={() => {
                        window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', { detail: { page: p, boundingBox: bbox } }));
                      }}
                      onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
                      className="px-1.5 py-0.5 bg-blue-400/20 text-blue-400 rounded text-xs border border-blue-400/30 cursor-pointer hover:bg-blue-400/30 transition-colors"
                    >
                      P{p}
                    </span>
                  );
                })}
              </span>
            )}
            <span
              className="text-xs eg-text-secondary cursor-pointer hover:text-blue-400 hover:underline"
              role="button"
              tabIndex={0}
              onClick={() => {
                window.dispatchEvent(new CustomEvent('navigateToAsBuiltPage', {
                  detail: { page: question.page_number, boundingBox: question.bounding_box ?? undefined },
                }));
              }}
              onKeyDown={(e) => e.key === 'Enter' && (e.target as HTMLElement).click()}
            >
              Page {question.page_number}
            </span>
            <span className="text-xs eg-text-secondary">{question.confidence}% confidence</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default CrossPageTab;
